/**
 * =============================================================================
 * STOX Theme - Collection Infinite Scroll
 * =============================================================================
 * 
 * Seamless infinite scroll pagination for collection pages.
 * Automatically loads more products as the user scrolls down.
 * 
 * FEATURES:
 * - Intersection Observer for performant scroll detection
 * - Seamless product loading without visible pagination
 * - Works with facet filtering system
 * - Loading state management
 * - SEO-friendly (preserves pagination URLs)
 * 
 * DEPENDENCIES:
 * - Requires data attributes on product grid and sentinel element
 * =============================================================================
 */

(() => {
  "use strict";

  // ==========================================================================
  // INITIALIZATION GUARD
  // ==========================================================================

  if (window.__infiniteScrollInitialized) return;
  window.__infiniteScrollInitialized = true;

  // ==========================================================================
  // CONFIGURATION
  // ==========================================================================

  const CONFIG = {
    // How far before the sentinel to start loading (in pixels from viewport)
    // Set very high to load products well before user reaches the bottom
    // 2000px = ~5 mobile screens, ensures even fast scrollers never see loading
    rootMargin: "2000px",
    // Minimum time between loads (debounce)
    loadDebounceMs: 50,
    // CSS selectors
    selectors: {
      productGrid: "[data-infinite-scroll-container]",
      sentinel: "[data-infinite-scroll-sentinel]",
      productItem: ".product-item-container",
      paginationInfo: "[data-infinite-scroll-pagination]",
      loadingIndicator: "[data-infinite-scroll-loading]"
    }
  };

  // ==========================================================================
  // INFINITE SCROLL CLASS
  // ==========================================================================

  class CollectionInfiniteScroll {
    constructor(container) {
      this.container = container;
      this.sentinel = container.querySelector(CONFIG.selectors.sentinel);
      this.paginationInfo = container.querySelector(CONFIG.selectors.paginationInfo);
      this.loadingIndicator = container.querySelector(CONFIG.selectors.loadingIndicator);
      
      if (!this.sentinel || !this.paginationInfo) {
        console.warn("Infinite scroll: Missing required elements");
        return;
      }

      this.currentPage = parseInt(this.paginationInfo.dataset.currentPage, 10) || 1;
      this.totalPages = parseInt(this.paginationInfo.dataset.totalPages, 10) || 1;
      this.baseUrl = this.paginationInfo.dataset.baseUrl || window.location.pathname;
      this.sectionId = this.paginationInfo.dataset.sectionId || "";
      
      this.isLoading = false;
      this.hasMorePages = this.currentPage < this.totalPages;
      
      this.init();
    }

    init() {
      if (!this.hasMorePages) {
        this.hideSentinel();
        return;
      }

      this.setupObserver();
    }

    setupObserver() {
      const options = {
        root: null,
        rootMargin: CONFIG.rootMargin,
        threshold: 0
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
            this.loadNextPage();
          }
        });
      }, options);

      this.observer.observe(this.sentinel);
    }

    async loadNextPage() {
      if (this.isLoading || !this.hasMorePages) return;

      this.isLoading = true;
      this.showLoading();

      const nextPage = this.currentPage + 1;
      
      try {
        const html = await this.fetchPage(nextPage);
        this.appendProducts(html);
        this.currentPage = nextPage;
        this.hasMorePages = this.currentPage < this.totalPages;

        // Update pagination info for potential facet refreshes
        if (this.paginationInfo) {
          this.paginationInfo.dataset.currentPage = this.currentPage;
        }

        // Note: We intentionally don't update the URL with ?page=X
        // This keeps the canonical URL clean and prevents Google from
        // indexing paginated URLs as separate pages

        if (!this.hasMorePages) {
          this.hideSentinel();
          this.observer?.disconnect();
        }
      } catch (error) {
        console.error("Infinite scroll: Error loading page", error);
      } finally {
        this.isLoading = false;
        this.hideLoading();
      }
    }

    async fetchPage(page) {
      // Build the URL with current filters and the new page
      const url = new URL(window.location.href);
      url.searchParams.set("page", page);
      
      // Add section_id for Shopify section rendering
      if (this.sectionId) {
        url.searchParams.set("section_id", this.sectionId);
      }

      const response = await fetch(url.toString(), {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.text();
    }

    appendProducts(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      
      // Find the product grid in the fetched HTML
      const newProductGrid = doc.querySelector(CONFIG.selectors.productGrid);
      if (!newProductGrid) {
        console.warn("Infinite scroll: Could not find product grid in response");
        return;
      }

      // Get all new product items
      const newProducts = newProductGrid.querySelectorAll(CONFIG.selectors.productItem);
      
      // Find the insertion point (before the sentinel)
      const grid = this.container.querySelector(".collection.grid") || 
                   this.container.querySelector(".grid");
      
      if (!grid) {
        console.warn("Infinite scroll: Could not find grid element");
        return;
      }

      // Create a document fragment for better performance
      const fragment = document.createDocumentFragment();
      
      newProducts.forEach((product) => {
        // Clone the product element - no animation, instant appearance
        // This makes the loading completely invisible to users
        const clone = product.cloneNode(true);
        fragment.appendChild(clone);
      });

      // Insert before the sentinel's parent container
      grid.insertBefore(fragment, this.sentinel);

      // Trigger any lazy loading observers for images
      this.triggerLazyLoad(grid);

      // Dispatch custom event for other scripts to hook into
      window.dispatchEvent(new CustomEvent("infiniteScroll:loaded", {
        detail: {
          page: this.currentPage + 1,
          productsAdded: newProducts.length
        }
      }));
    }

    triggerLazyLoad(container) {
      // Trigger intersection observer for lazy-loaded images
      const lazyImages = container.querySelectorAll("img[loading='lazy']");
      lazyImages.forEach((img) => {
        if (img.dataset.src && !img.src) {
          img.src = img.dataset.src;
        }
      });
    }

    // URL updating disabled to prevent Google from indexing ?page=X URLs
    // The base collection URL stays clean, and Google discovers products
    // through the hidden rel="next/prev" pagination links
    // updateUrl(page) { ... }

    showLoading() {
      // Loading indicator hidden for seamless UX - products load before user sees them
      // Keeping method for potential future use
    }

    hideLoading() {
      // Loading indicator hidden for seamless UX
      // Keeping method for potential future use
    }

    hideSentinel() {
      if (this.sentinel) {
        this.sentinel.classList.add("hidden");
      }
    }

    // Method to reset infinite scroll (called after facet filtering)
    reset(newPaginationData) {
      this.currentPage = newPaginationData.currentPage || 1;
      this.totalPages = newPaginationData.totalPages || 1;
      this.hasMorePages = this.currentPage < this.totalPages;
      this.isLoading = false;

      if (this.paginationInfo) {
        this.paginationInfo.dataset.currentPage = this.currentPage;
        this.paginationInfo.dataset.totalPages = this.totalPages;
      }

      if (this.hasMorePages) {
        if (this.sentinel) {
          this.sentinel.classList.remove("hidden");
        }
        // Reconnect observer if it was disconnected
        if (!this.observer) {
          this.setupObserver();
        } else {
          this.observer.observe(this.sentinel);
        }
      } else {
        this.hideSentinel();
        this.observer?.disconnect();
      }
    }

    destroy() {
      this.observer?.disconnect();
      this.observer = null;
    }
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  function initInfiniteScroll() {
    const containers = document.querySelectorAll(CONFIG.selectors.productGrid);
    
    containers.forEach((container) => {
      // Skip if already initialized
      if (container.__infiniteScroll) return;
      
      container.__infiniteScroll = new CollectionInfiniteScroll(container);
    });
  }

  // ==========================================================================
  // FACET INTEGRATION
  // ==========================================================================

  /**
   * Destroy all existing infinite scroll instances and reinitialize.
   * This is necessary after facet filtering because the facets system replaces
   * the innerHTML of the product grid container, leaving stale __infiniteScroll
   * instances that reference DOM elements that no longer exist.
   */
  function reinitializeInfiniteScroll() {
    const containers = document.querySelectorAll(CONFIG.selectors.productGrid);
    containers.forEach((container) => {
      if (container.__infiniteScroll) {
        container.__infiniteScroll.destroy();
        delete container.__infiniteScroll;
      }
    });
    
    // Reinitialize with fresh DOM references
    initInfiniteScroll();
  }

  // Listen for facet filter updates and reset infinite scroll
  function setupFacetIntegration() {
    // Listen for the facets:updated event dispatched by facets.js
    // after renderProductGridContainer replaces the product grid innerHTML.
    // We must destroy and reinitialize because:
    // 1. FacetFiltersForm is defined inside an IIFE in facets.js and is NOT
    //    exposed on window, so we cannot hook into its methods directly
    // 2. The container element itself is preserved (only innerHTML is replaced),
    //    so the stale __infiniteScroll property persists with references to
    //    DOM elements that no longer exist
    document.addEventListener("facets:updated", () => {
      // Small delay to ensure DOM is fully updated
      setTimeout(reinitializeInfiniteScroll, 100);
    });
  }

  // ==========================================================================
  // DOM READY
  // ==========================================================================

  function onReady(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  }

  onReady(() => {
    initInfiniteScroll();
    setupFacetIntegration();
  });

  // Re-initialize on Shopify theme editor events
  if (window.Shopify?.designMode) {
    document.addEventListener("shopify:section:load", initInfiniteScroll);
  }

  // Expose for external use
  window.CollectionInfiniteScroll = {
    init: initInfiniteScroll,
    CONFIG
  };

})();
